import React, { useContext, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { claimsAdTools, searchClaims } from "../services/ClaimsServices";
import { AuthContext } from "../AuthContext";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileOpenIcon from '@mui/icons-material/FileOpen';

export const ClaimsAdminTool = () => {
  const { user } = useContext(AuthContext);
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const openConfirmation = (actionType, payload) => {
    setConfirmAction(actionType); // 'status' | 'stDataPull' | 'lastSyncDate'
    setConfirmPayload(payload); // Contains rowId and newValue
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction || !confirmPayload) return;
    setConfirmLoading(true);
    const { rowId, newValue } = confirmPayload;
    try {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === rowId
            ? {
                ...row,
                [confirmAction]: newValue,
              }
            : row
        )
      );

      if (confirmAction === "Revive Claim") {
        await handleReviveClaim(rowId);
      } else if (confirmAction === "Duplicate Claim") {
        await handleDuplicateClaim(rowId);
      }

      setConfirmOpen(false);
      setConfirmAction(null);
      setConfirmPayload(null);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const res = await searchClaims(searchText);
      const data = res.data;
      console.log("claim search :", data);

      if (data?.length) {
        setRows(data);
        setNoResults(false);
      } else {
        setRows([]);
        setNoResults(true);
      }
    } catch (e) {
      console.log("claim search error :", e);
      setRows([]);
      setNoResults(true);
    }
    setLoading(false);
  };

  const columns = [
    {
      field: "dealerId",
      headerName: "Dealer Id",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jobNumber",
      headerName: "Job Number",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
        {
      field: "invoiceId",
      headerName: "Invoice #",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jbwAgreementId",
      headerName: "Agreement Id",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "customerName",
      headerName: "CustomerName",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "Address",
      headerName: "Address",
      width: 180,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jbwClaimId",
      headerName: "JBW Claim ID",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "claimAmount",
      headerName: "Amount",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "claimStatus",
      headerName: "Claim Status",
      width: 200,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      cellClassName: "actions",
      width: 80,
      headerAlign: "center",
      align: "center",
      getActions: (params) => {
        //console.log("params", params.row);
        const ignoreInvoice = params.row.ignoreInvoice;
        let cellItems = [];
        //isreadyfor jwd = 0
        if (ignoreInvoice === 1) {
          cellItems.push(
            <GridActionsCellItem
              icon={<FlipCameraAndroidIcon fontSize="medium" />}
              label="Revive Claim"
              className="textPrimary"
              onClick={() => {
                openConfirmation("Revive Claim", {
                  rowId: params.row.id,
                  newValue: 0,
                });
              }}
              color="secondary"
              title="Revive Claim"
            />
          );
        } else {
          cellItems.push(
            <GridActionsCellItem
              icon={<ContentCopyIcon fontSize="medium" />}
              label="Duplicate Claim"
              className="textPrimary"
              onClick={() => {
                openConfirmation("Duplicate Claim", {
                  rowId: params.row.id,
                  newValue: params.row,
                });
              }}
              color="error"
              title="Duplicate Claim"
            />
          );
          // cellItems.push(
          //   <GridActionsCellItem
          //     icon={<FileOpenIcon fontSize="medium" />}
          //     label="View Claim"
          //     className="textPrimary"
          //     onClick={handleViewClaim(params)}
          //     color="primary"
          //     title="View Claim"
          //   />
          // );
        }
        return cellItems;
      },
      sortable: false,
    },
  ];

  const handleReviveClaim = async (rowId) => {
    console.log("revive claim called", rowId);
    const result = await claimsAdTools(rowId, "revive", user.name);
    console.log("Revive Result :", result);
    await handleSearch();
  };

  const handleDuplicateClaim = async (rowId) => {
    console.log("Duplicate claim called", rowId);
    const result = await claimsAdTools(rowId, "duplicate", user.name);
    console.log("duplicate Result :", result);
    await handleSearch();
  };

  const handleViewClaim = (param) => () => {
    console.log("View claim called", param);
  };

  const QuickSearchToolbar = () => {
    return (
      <Box
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarQuickFilter />
      </Box>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          variant="outlined"
          placeholder="Claim Search"
          value={searchText}
          onKeyDown={handleKeyDown}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch}>
                  {loading ? (
                    <CircularProgress size={20} color="warning" />
                  ) : (
                    <SearchIcon color="secondary" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              width: 450,
              height: 40,
              paddingLeft: 1,
              borderRadius: "20px",
            },
          }}
        />
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm {confirmAction}</DialogTitle>
        <DialogContent>
          Are you sure you want to <strong>{confirmAction}</strong>?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="inherit"
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="primary"
            variant="contained"
            disabled={confirmLoading}
            startIcon={
              confirmLoading && <CircularProgress size={20} color="inherit" />
            }
          >
            {confirmLoading ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {noResults ? (
        <Typography variant="body1" color="red">
          Job Number not found in the system. Please contact support team for
          further help
        </Typography>
      ) : (
        <Box sx={{ height: 400 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableColumnFilter={true}
            disableColumnMenu={true}
            getRowId={(row) => `${row.id}-${row.dealerId}`}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              toolbar: QuickSearchToolbar,
            }}
          />
        </Box>
      )}
    </>
  );
};
